/**
 * AutoSaveIndicator — Editorial pill in the bottom-right corner (Fase 5, Feb 2026).
 *
 * Reads global status from AutoSaveContext and renders one of three states:
 *   saving  → "Lagrer…" with a subtle spinning loader in ink/50
 *   saved   → "Lagret" with a fjord check icon (auto-dismisses via hook)
 *   error   → "Kunne ikke lagre" — persists until next successful save (rolig red)
 *
 * Redesign vs. legacy
 *   - Bytte fra dark charcoal/emerald pill → editorial `bg-paper border border-ink/15`
 *     for saving/saved states; rolig `border-red-700/40 bg-paper` for error.
 *   - Ingen backdrop-blur, ingen tunge shadows — kun hairline border + subtle
 *     drop-shadow for lift.
 *   - Fonts: `font-mono text-[10px] uppercase tracking-[0.24em]`.
 */
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Check, Loader2 } from 'lucide-react';

import { useAutoSaveStatus } from '../contexts/AutoSaveContext';

const AutoSaveIndicator = () => {
  const { status, lastError } = useAutoSaveStatus();

  const visible = status !== 'idle';
  const isError = status === 'error';

  const label =
    status === 'saving'
      ? 'Lagrer…'
      : status === 'saved'
      ? 'Lagret'
      : status === 'error'
      ? 'Kunne ikke lagre'
      : '';

  const Icon =
    status === 'saving' ? Loader2 : status === 'saved' ? Check : AlertCircle;

  // Editorial state palette — no emerald, no dark chip.
  const containerClass = isError
    ? 'bg-paper border border-red-700/30 text-red-700'
    : 'bg-paper border border-ink/12 text-ink';

  const iconClass =
    status === 'saved'
      ? 'text-fjord'
      : status === 'error'
      ? 'text-red-700'
      : 'text-ink/50';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="autosave-indicator"
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-6 right-6 z-[70] pointer-events-none select-none"
          data-testid="autosave-indicator"
          data-status={status}
          style={{ boxShadow: '0 4px 14px -6px rgba(10,14,27,0.12)' }}
        >
          <div className={`rounded-full px-4 py-2.5 flex items-center gap-2.5 ${containerClass}`}>
            <Icon
              size={13}
              strokeWidth={1.8}
              className={`${iconClass} ${status === 'saving' ? 'animate-spin' : ''}`}
              data-testid="autosave-indicator-icon"
            />
            <span
              className="font-mono text-[10px] uppercase tracking-[0.24em] font-medium"
              data-testid="autosave-indicator-label"
            >
              {label}
            </span>
            {isError && lastError && (
              <span
                className="font-mono text-[10px] opacity-70 max-w-[240px] truncate ml-1"
                title={lastError}
                data-testid="autosave-indicator-error-detail"
              >
                · {lastError}
              </span>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AutoSaveIndicator;
